import { jsx } from '@amazon/vinyl-tsx'
import { type MaybeObservableValue, type MutableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { createDisposer, type Maybe } from '@amazon/vinyl-util'
import { windowEvents } from '@/util/interaction'
import { navigateTo } from '@/router/router'
import { toast, toastError } from '@/components/toast'
import type { TrackFields } from '@/track/trackFieldsFrag'
import { getString } from '@/track/i18n/bundle.generated'
import type { PlayQueueBindingDeps } from '@/playback/playQueueBinding'
import {
    addEntityToCloudQueue,
    insertEntityIntoCloudQueue,
    setCloudQueueLoopMode,
    setCloudQueueShuffleMode,
} from '@/cloudQueue/cloudQueueService'
import { setTrackLikeState } from '@/track/trackLikeService'
import { ctx } from '@/util/context'

export interface TrackOverflowMenuProps {
    readonly visible: MutableValue<boolean>
    /**
     * Track the menu acts on. Captured at open time so a row that
     * scrolls out of the virtual list while the menu is up still has
     * a stable target.
     */
    readonly track: MutableValue<Maybe<TrackFields>>
    /**
     * Anchor button — used so an outside-click that lands on the
     * button itself doesn't cancel the user's toggle. Resolved
     * lazily because the button is rendered as a sibling and may
     * not be attached yet when the menu is constructed.
     */
    readonly getAnchor: () => HTMLElement | null
    /**
     * Invoked when the Share row is selected. Opens the parent's
     * ShareModal so the track gets the same share UI as albums and
     * playlists.
     */
    readonly onShare: () => void
    /**
     * Invoked when the Add to Playlist row is selected. Opens the
     * parent's AddToPlaylistModal pre-bound to the captured track.
     */
    readonly onAddToPlaylist: () => void
    /**
     * Optional. When passed, Play Next + Add to Queue route through
     * the live cloud queue id and the current entity reference id
     * tracked on `playlistController.cloudQueueOptions` /
     * `playlistController.current`. Without it those rows toast a
     * "no queue" error.
     */
    readonly deps?: PlayQueueBindingDeps
    /**
     * When true, the menu surfaces compact-only items at the top
     * (Previous / Shuffle / Repeat) — hidden at wider widths via
     * CSS media query, used when the transport's inline buttons are
     * collapsed. Only the transport overflow opts in; row-level
     * overflows pass false.
     */
    readonly transport?: boolean
}

/**
 * Floating menu surfaced from the track-row overflow button. Mirrors
 * green hornet's track overflow: Play Next / Add to Queue / Add to
 * Playlist for queue actions (TODO: wire to real mutations), and
 * View Album / View Artist / Share for nav actions.
 */
export function TrackOverflowMenu(props: TrackOverflowMenuProps) {
    const { errorHandler } = ctx('TrackOverflowMenu')

    const close = () => {
        props.visible.value = false
    }

    function getCurrentQueueContext(): {
        readonly queueId: string
        readonly anchorRef: string | null
    } | null {
        const controller = props.deps?.playlistController
        if (!controller) return null
        const queueId = controller.cloudQueueOptions.value?.queueId
        if (!queueId) return null
        const anchorRef = controller.current.value?.entityReferenceId ?? null
        return { queueId, anchorRef }
    }

    function playNext() {
        close()
        const asin = props.track.value?.playParams?.id
        if (!asin) return
        const queueCtx = getCurrentQueueContext()
        if (!queueCtx) {
            toastError('No active queue — start something playing first.')
            return
        }
        if (!queueCtx.anchorRef) {
            // No current track yet — fall back to addToQueue, since
            // insertIntoQueue requires an anchor.
            addEntityToCloudQueue(queueCtx.queueId, asin)
                .then(() => {
                    toast(getString('dmusic_track_add_to_queue_toast'))
                    props.deps?.playlistController.notifyQueueMutated()
                })
                .catch((err) => {
                    toastError(getString('dmusic_track_add_to_queue_toast_failure'))
                    errorHandler(err)
                })
            return
        }
        insertEntityIntoCloudQueue(queueCtx.queueId, asin, queueCtx.anchorRef, 'AFTER')
            .then(() => {
                toast('Playing next')
                props.deps?.playlistController.notifyQueueMutated()
            })
            .catch((err) => {
                toastError("Couldn't queue track")
                errorHandler(err)
            })
    }

    function addToQueue() {
        close()
        const asin = props.track.value?.playParams?.id
        if (!asin) return
        const queueCtx = getCurrentQueueContext()
        if (!queueCtx) {
            toastError('No active queue — start something playing first.')
            return
        }
        addEntityToCloudQueue(queueCtx.queueId, asin)
            .then(() => {
                toast('Added to queue')
                props.deps?.playlistController.notifyQueueMutated()
            })
            .catch((err) => {
                toastError("Couldn't add to queue")
                errorHandler(err)
            })
    }

    function viewAlbum() {
        const id = props.track.value?.album?.id
        close()
        if (!id) {
            toast("Couldn't find this album")
            return
        }
        navigateTo(`/albums/${id}`)
    }

    function viewArtist() {
        const id = props.track.value?.contributingArtists?.edges?.[0]?.node?.id
        close()
        if (!id) {
            toast("Couldn't find this artist")
            return
        }
        navigateTo(`/artists/${id}`)
    }

    // Compact-mode helpers — only fire when this menu is hosting
    // the transport overflow at narrow widths. Each one updates the
    // local controller flag, pushes the change to the cloud queue,
    // and rolls back the flag on failure (mirrors the inline button
    // behaviour over in Transport.tsx).
    function skipPrevious() {
        close()
        const controller = props.deps?.playlistController
        if (!controller) return
        controller.skipPrevious().catch((err) => {
            toastError("Couldn't go back")
            errorHandler(err)
        })
    }

    function toggleShuffle() {
        close()
        const controller = props.deps?.playlistController
        if (!controller) return
        const next = !controller.shuffleEnabled.value
        controller.shuffleEnabled.value = next
        const queueId = controller.cloudQueueOptions.value?.queueId
        if (!queueId) return
        const entityRef = controller.current.value?.entityReferenceId
        setCloudQueueShuffleMode(queueId, next ? 'SHUFFLE_ON' : 'SHUFFLE_OFF', entityRef)
            .then(() => props.deps?.cloudQueueController.refetchQueue())
            .catch((err) => {
                toastError("Couldn't update shuffle")
                errorHandler(err)
                controller.shuffleEnabled.value = !next
            })
    }

    function toggleLike() {
        close()
        const v = props.track.value
        if (!v?.id) return
        const previous = v.likesContext?.liked ?? false
        const next = !previous
        // Optimistic flip via the writable projection — assigning
        // `props.track.value = {...}` fails when track is a derived
        // (non-writable) observable (e.g. when the transport derives
        // currentTrack via `.map(...)` from currentEntity). The
        // `liked` projection is writable because TrackFields exposes
        // it as a normal field, and the assignment notifies up the
        // chain. Mirrors TrackLikeButton's behaviour exactly.
        const liked = props.track.pick('likesContext').pick('liked')
        liked.value = next
        toast(getString(next ? 'dmusic_track_liked_alert_text' : 'dmusic_track_unliked_alert_text'))
        setTrackLikeState({ trackId: v.id, likeState: next ? 'LIKE' : 'DISLIKE' }).catch((err) => {
            liked.value = previous
            errorHandler(err)
        })
    }

    function shuffleAllowed(): boolean {
        const controller = props.deps?.playlistController
        if (!controller) return false
        return controller.queueCapabilities.value?.shuffleAllowed !== false
    }

    function loopAllowed(): boolean {
        const controller = props.deps?.playlistController
        if (!controller) return false
        return controller.queueCapabilities.value?.loopAllAllowed !== false
    }

    function cycleRepeat() {
        close()
        const controller = props.deps?.playlistController
        if (!controller) return
        const previous = controller.repeatMode.value
        const nextMode = previous === 'off' ? 'all' : previous === 'all' ? 'one' : 'off'
        controller.repeatMode.value = nextMode
        const queueId = controller.cloudQueueOptions.value?.queueId
        if (!queueId) return
        const loopArg =
            nextMode === 'one' ? 'LOOP_ONE' : nextMode === 'all' ? 'LOOP_ALL' : 'LOOP_OFF'
        const entityRef = controller.current.value?.entityReferenceId
        setCloudQueueLoopMode(queueId, loopArg, entityRef)
            .then(() => props.deps?.cloudQueueController.refetchQueue())
            .catch((err) => {
                toastError("Couldn't update repeat")
                errorHandler(err)
                controller.repeatMode.value = previous
            })
    }

    function share() {
        const asin = props.track.value?.playParams?.id
        if (!asin) {
            close()
            toastError("Couldn't share this track")
            return
        }
        // Open the share modal FIRST, then close the menu. Closing
        // first triggers vinyl-tsx's `hide(view)` synchronously
        // mid-bubble, which would mutate the DOM before the click
        // event has fully unwound — `props.onShare()` sometimes
        // landed too late in that ordering and the modal's
        // `visible.onData(true)` callback didn't run before
        // microtasks for the click event drained. Order matters.
        props.onShare()
        close()
    }

    type IconArg = Parameters<typeof Icon>[0]['icon']
    /**
     * `label` accepts either a static string or an `ObservableValue<string>`
     * — the bundle is loaded async, so passing the observable makes the
     * row label update once the strings land. Passing `.value` instead
     * (the previous behavior) snapshotted the placeholder empty string
     * before load and never refreshed.
     */
    const item = (
        icon: IconArg,
        label: MaybeObservableValue<string>,
        onSelect: () => void,
        disabled = false
    ) => (
        <button className="trackOverflowMenuItem" disabled={disabled} onclick={() => onSelect()}>
            <Icon icon={icon} className="trackOverflowMenuItemIcon" />
            <span className="trackOverflowMenuItemLabel">{label}</span>
        </button>
    )

    // Anchor rect is captured ONCE at open time and the menu is then
    // pinned in viewport coords. Recomputing on scroll caused the
    // menu to jump as the virtualized row recycles or briefly reports
    // off-screen rects. Resize is the only event that warrants
    // re-running the layout (window dimensions change).
    function position(view: HTMLElement) {
        const anchor = props.getAnchor()
        if (!anchor) return
        const rect = anchor.getBoundingClientRect()
        const menuW = view.offsetWidth || 220
        const menuH = view.offsetHeight || 0
        const margin = 6
        const safe = 8

        // Prefer dropping below the anchor; flip above if there isn't
        // room. After both attempts, clamp to the viewport so the
        // menu is always fully visible.
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        let top: number
        if (spaceBelow >= menuH + margin + safe || spaceBelow >= spaceAbove) {
            top = rect.bottom + margin
        } else {
            top = rect.top - margin - menuH
        }
        top = Math.max(safe, Math.min(top, window.innerHeight - menuH - safe))

        // Right-align with the anchor, clamp to viewport.
        let left = rect.right - menuW
        left = Math.max(safe, Math.min(left, window.innerWidth - menuW - safe))

        view.style.top = `${top}px`
        view.style.left = `${left}px`
    }

    return (
        <div
            className="trackOverflowMenu"
            visible={props.visible}
            onConnect={(view) => {
                // Reparent to <body> so the menu escapes any
                // ancestor that clips overflow (virtual lists,
                // detail-page hero crops, etc).
                const originalParent = view.parentNode
                const placeholder = document.createComment('trackOverflowMenu')
                if (originalParent) originalParent.insertBefore(placeholder, view)
                document.body.appendChild(view)

                const { add, dispose } = createDisposer()
                add(
                    props.visible.onData((visible) => {
                        if (visible) position(view)
                    })
                )
                add(
                    windowEvents.on('resize', () => {
                        if (props.visible.value) position(view)
                    })
                )
                // No scroll listener: position is captured at open
                // time and stays fixed in viewport coords until the
                // user dismisses or resizes. Re-running position()
                // on scroll caused the menu to jump because the
                // anchor row lives inside a virtualized list whose
                // rect briefly reports stale / off-screen values
                // as items recycle.
                add(
                    windowEvents.on('click', (event) => {
                        if (!props.visible.value) return
                        const target = event.target as Node
                        if (view.contains(target)) return
                        const anchor = props.getAnchor()
                        if (anchor && anchor.contains(target)) return
                        close()
                    })
                )
                add(
                    windowEvents.on('keydown', (event) => {
                        if (event.key === 'Escape' && props.visible.value) {
                            event.preventDefault()
                            close()
                        }
                    })
                )
                add(() => {
                    // Restore the menu to its original location so
                    // vinyl-tsx can clean it up normally.
                    if (placeholder.parentNode) {
                        placeholder.parentNode.insertBefore(view, placeholder)
                        placeholder.remove()
                    } else if (view.parentNode === document.body) {
                        document.body.removeChild(view)
                    }
                })
                return dispose
            }}
        >
            {props.transport && (
                <div className="trackOverflowCompactGroup">
                    {item(
                        props.track.map((t) =>
                            t?.likesContext?.liked ? 'favoriteOn' : 'favoriteOff'
                        ),
                        props.track.map((t) =>
                            t?.likesContext?.liked
                                ? 'Remove from liked songs'
                                : 'Add to liked songs'
                        ),
                        toggleLike
                    )}
                    {item('previous', 'Previous', skipPrevious, !props.deps)}
                    {item(
                        'shuffle',
                        getString('dmusic_transport_playback_shuffle_accessibility_label'),
                        toggleShuffle,
                        !shuffleAllowed()
                    )}
                    {item(
                        'repeat',
                        getString('dmusic_transport_playback_repeat_accessibility_label'),
                        cycleRepeat,
                        !loopAllowed()
                    )}
                    <div className="trackOverflowMenuDivider" />
                </div>
            )}
            {item('queueNext', getString('dmusic_track_overflow_play_next'), playNext, !props.deps)}
            {item(
                'addToQueue',
                getString('dmusic_track_overflow_add_to_queue_text'),
                addToQueue,
                !props.deps
            )}
            {item('addToPlaylist', getString('dmusic_track_overflow_add_to_playlist_text'), () => {
                close()
                if (!props.track.value?.playParams?.id) {
                    toast("Couldn't find this track")
                    return
                }
                props.onAddToPlaylist()
            })}
            <div className="trackOverflowMenuDivider" />
            {item('album', getString('dmusic_track_overflow_view_album_text'), viewAlbum)}
            {item('artist', getString('dmusic_track_overflow_view_artist_text'), viewArtist)}
            <div className="trackOverflowMenuDivider" />
            {item('shareLink', getString('dmusic_track_overflow_share_text'), share)}
        </div>
    )
}
